<template>
  <v-app>
    <v-main>
      <router-view />
    </v-main>

    <!-- Global Unsaved Changes Modal -->
    <UnsavedChangesModal
      v-model="store.showUnsavedChangesModal"
      :person-name="store.personName"
      @confirm="store.confirmNavigation"
      @cancel="store.cancelNavigation"
    />

    <v-snackbar
      v-model="showOverwriteWarning"
      color="warning"
      location="top"
      :timeout="10000"
    >
      Your changes were saved, but someone else updated this memory after you opened it. Their newer changes may have been overwritten.
      <template #actions>
        <v-btn variant="text" @click="showOverwriteWarning = false">Close</v-btn>
      </template>
    </v-snackbar>

    <!-- Keep focused public content pages free of global chrome. -->
    <SupportWidget v-if="!isSlideShowRoute && !isGuidesRoute" />

    <AppFooter v-if="!isSlideShowRoute && !isGuidesRoute" />

    <!-- Version Display -->
    <div class="app-version">v{{ appVersion }}</div>
  </v-app>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useMemorialStore } from './stores/memorial'
import UnsavedChangesModal from './components/UnsavedChangesModal.vue'
import SupportWidget from './components/support/SupportWidget.vue'
import AppFooter from './components/AppFooter.vue'
import { APP_VERSION } from './version'
import { MEMORIAL_SAVE_EVENT } from './services/api'

const store = useMemorialStore()
const appVersion = APP_VERSION
const route = useRoute()
const showOverwriteWarning = ref(false)

const handleMemorialSave = (event) => {
  if (event.detail?.version != null) {
    store.setMemorialVersion(event.detail.version)
  }

  if (event.detail?.staleOverwrite) {
    showOverwriteWarning.value = true
  }
}

onMounted(() => window.addEventListener(MEMORIAL_SAVE_EVENT, handleMemorialSave))
onBeforeUnmount(() => window.removeEventListener(MEMORIAL_SAVE_EVENT, handleMemorialSave))

const isSlideShowRoute = computed(() =>
  route.name === 'SlideShow' || route.name === 'VideoSlideShow'
)
const isGuidesRoute = computed(() => route.meta.guides === true)
</script>

<style>
/* Global heading styles with Alice font - only h1-h4 */
h1, h2, h3, h4,
.v-card-title,
.text-h1, .text-h2, .text-h3, .text-h4 {
  font-family: var(--v-theme-headingFont) !important;
}

/* h5, h6 and smaller headings use regular body font */
h5, h6,
.text-h5, .text-h6,
.text-subtitle-1, .text-subtitle-2 {
  font-family: 'Roboto', sans-serif !important;
}

/* Global brand logo SVG styles */
.brand-logo-svg {
  height: 50px;
  width: auto;
  fill: var(--v-theme-primary);
}

@media (max-width: 600px) {
  .brand-logo-svg {
    height: 35px;
  }
}

/* Page headers: let long memorial names shrink/truncate instead of overflowing */
.page-header .brand-header,
.page-header .brand-header a {
  min-width: 0;
  max-width: 100%;
}

/* On small screens, keep the header text clear of the profile avatar in the top-right corner */
@media (max-width: 600px) {
  .page-header {
    padding-left: 48px;
    padding-right: 48px;
  }
}

/* Version display in bottom right corner */
.app-version {
  position: fixed;
  bottom: 8px;
  right: 8px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.2);
  font-family: monospace;
  pointer-events: none;
  z-index: 9999;
  user-select: none;
}
</style>
